import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
  type BadgeTone,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { api, isApiError } from '@/lib/api';
import { whenLabel } from '@/pages/calendar/time';
import styles from './parity.module.css';

type Report = 'daily_closing' | 'panel_statement';
interface DispatchConfig {
  provider: string | null;
  dailyClosing: { enabled: boolean; time: string; recipients: string[] };
  panelStatements: { enabled: boolean; dayOfMonth: number; time: string };
}
export interface Dispatch {
  id: string;
  report: Report;
  period: string;
  recipient: string | null;
  channel: string;
  status: 'queued' | 'sent' | 'failed' | 'unavailable';
  reason: string | null;
  summary: Record<string, unknown>;
  trigger: string;
  createdAt: string;
  createdBy: string | null;
}
export interface DispatchOverview {
  config: DispatchConfig;
  deliveryAvailable: boolean;
  deliveryNote: string | null;
  history: Dispatch[];
}

const STATUS: Record<Dispatch['status'], { label: string; tone: BadgeTone }> = {
  queued: { label: 'Queued', tone: 'info' },
  sent: { label: 'Sent', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  unavailable: { label: 'Not sent · unavailable', tone: 'warning' },
};

/**
 * RL-1033 report dispatch panel (prototype "Sent to the owners at 22:30" and
 * "Monthly statements"): settings, Send now and the recorded history. With
 * no email provider every send is recorded as unavailable; nothing is shown
 * as sent unless the API says so.
 */
export function ReportDispatchPanel({
  report,
  day,
}: {
  report: Report;
  day?: string;
}) {
  const { hasPermission } = useSession();
  const { selected } = useBranch();
  const toast = useToast();
  const qc = useQueryClient();
  const [settings, setSettings] = useState(false);
  const canSend =
    report === 'daily_closing'
      ? hasPermission('report_dispatch:manage')
      : hasPermission('claims:manage');
  const q = useQuery({
    queryKey: ['report-dispatch', report, selected?.id ?? 'all'],
    queryFn: ({ signal }) =>
      api
        .get<DispatchOverview>('/reports/dispatch', {
          signal,
          query: { report },
        })
        .then((r) => r.data),
  });
  const send = useMutation({
    mutationFn: () =>
      api
        .post<{
          sends: Pick<Dispatch, 'id' | 'recipient' | 'status' | 'reason'>[];
        }>(
          report === 'daily_closing'
            ? '/reports/dispatch/daily-closing'
            : '/reports/dispatch/statements',
          report === 'daily_closing' && day ? { day } : {},
        )
        .then((r) => r.data),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['report-dispatch'] }),
  });
  const d = q.data;
  const c = d?.config;
  const schedule =
    report === 'daily_closing'
      ? c &&
        `Every night at ${c.dailyClosing.time} to ${c.dailyClosing.recipients.length ? c.dailyClosing.recipients.join(', ') : 'no recipients yet'}`
      : c &&
        `On day ${c.panelStatements.dayOfMonth} of each month at ${c.panelStatements.time}, to each panel's billing email`;
  const auto =
    report === 'daily_closing'
      ? c?.dailyClosing.enabled
      : c?.panelStatements.enabled;
  return (
    <Card
      title={
        report === 'daily_closing' ? 'Send to the owners' : 'Monthly statements'
      }
      flush
      actions={
        <div className={styles.rowActions}>
          {hasPermission('report_dispatch:manage') && (
            <Button size="sm" variant="ghost" onClick={() => setSettings(true)}>
              Settings
            </Button>
          )}
          {canSend && (
            <Button
              size="sm"
              loading={send.isPending}
              disabled={!d}
              onClick={() =>
                void send
                  .mutateAsync()
                  .then((r) => {
                    const n = r.sends.length;
                    const sent = r.sends.filter(
                      (s) => s.status === 'sent' || s.status === 'queued',
                    ).length;
                    toast.show(
                      sent
                        ? { tone: 'success', title: `${sent} of ${n} queued` }
                        : {
                            tone: 'info',
                            title: n
                              ? `Not sent: recorded ${n} as unavailable`
                              : 'Nothing to send',
                            body: n
                              ? (r.sends[0].reason ?? undefined)
                              : undefined,
                          },
                    );
                  })
                  .catch((err: unknown) =>
                    toast.show({
                      tone: 'error',
                      title: 'Not recorded',
                      body: isApiError(err) ? err.message : undefined,
                      error: err,
                    }),
                  )
              }
            >
              Send now
            </Button>
          )}
        </div>
      }
    >
      <div style={{ padding: '14px 18px' }}>
        {d?.deliveryNote && <p className={styles.notice}>{d.deliveryNote}</p>}
        <dl className={styles.kv}>
          <dt>Schedule</dt>
          <dd>
            {schedule ?? '—'}
            <span className={styles.sub}>
              {auto
                ? d?.deliveryAvailable
                  ? 'Automatic sending is on.'
                  : 'Automatic sending is on, but waits for a delivery provider.'
                : 'Automatic sending is off.'}
            </span>
          </dd>
        </dl>
      </div>
      <DataTable
        caption={
          report === 'daily_closing' ? 'Daily closing sends' : 'Statement sends'
        }
        rows={d?.history}
        rowKey={(h) => h.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={<EmptyState title="Nothing sent yet" />}
        columns={[
          { key: 'w', header: 'When', render: (h) => whenLabel(h.createdAt) },
          {
            key: 'p',
            header: report === 'daily_closing' ? 'Day' : 'Panel',
            render: (h) =>
              report === 'daily_closing'
                ? h.period
                : String(h.summary.panel ?? '—'),
          },
          { key: 'r', header: 'To', render: (h) => h.recipient ?? '—' },
          {
            key: 's',
            header: 'Status',
            render: (h) => (
              <>
                <Badge tone={STATUS[h.status].tone}>
                  {STATUS[h.status].label}
                </Badge>
                {h.reason && <span className={styles.sub}>{h.reason}</span>}
              </>
            ),
          },
          {
            key: 'b',
            header: 'By',
            render: (h) =>
              h.createdBy ?? (h.trigger === 'scheduled' ? 'Schedule' : '—'),
          },
        ]}
      />
      {settings && c && (
        <SettingsDialog c={c} onClose={() => setSettings(false)} />
      )}
    </Card>
  );
}

function SettingsDialog({
  c,
  onClose,
}: {
  c: DispatchConfig;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({
    dcEnabled: c.dailyClosing.enabled ? 'on' : 'off',
    dcTime: c.dailyClosing.time,
    recipients: c.dailyClosing.recipients.join('\n'),
    psEnabled: c.panelStatements.enabled ? 'on' : 'off',
    psDay: String(c.panelStatements.dayOfMonth),
    psTime: c.panelStatements.time,
  });
  const save = useMutation({
    mutationFn: () =>
      api.put('/reports/dispatch/config', {
        dailyClosing: {
          enabled: v.dcEnabled === 'on',
          time: v.dcTime,
          recipients: v.recipients
            .split(/[\n,]/)
            .map((r) => r.trim())
            .filter(Boolean),
        },
        panelStatements: {
          enabled: v.psEnabled === 'on',
          dayOfMonth: Number(v.psDay),
          time: v.psTime,
        },
      }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['report-dispatch'] }),
  });
  const onOff = [
    { value: 'off', label: 'Off' },
    { value: 'on', label: 'On' },
  ];
  return (
    <Dialog
      open
      title="Report sending"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() =>
              void save
                .mutateAsync()
                .then(() => {
                  toast.show({ tone: 'success', title: 'Saved' });
                  onClose();
                })
                .catch((err: unknown) =>
                  toast.show({
                    tone: 'error',
                    title: 'Not saved',
                    body: isApiError(err) ? err.message : undefined,
                    error: err,
                  }),
                )
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <p className={styles.notice}>
          Recipients are the clinic owners&apos; choice. No email provider is
          connected yet; settings take effect once it is.
        </p>
        <Field label="Daily closing: automatic">
          <Select
            value={v.dcEnabled}
            onChange={(e) => setV({ ...v, dcEnabled: e.target.value })}
            options={onOff}
          />
        </Field>
        <Field label="Daily closing: time">
          <Input
            type="time"
            value={v.dcTime}
            onChange={(e) => setV({ ...v, dcTime: e.target.value })}
          />
        </Field>
        <Field label="Daily closing: recipients (one email per line)">
          <textarea
            className={styles.textarea}
            rows={3}
            value={v.recipients}
            onChange={(e) => setV({ ...v, recipients: e.target.value })}
          />
        </Field>
        <Field label="Panel statements: automatic">
          <Select
            value={v.psEnabled}
            onChange={(e) => setV({ ...v, psEnabled: e.target.value })}
            options={onOff}
          />
        </Field>
        <Field label="Panel statements: day of month (1 to 28)">
          <Input
            type="number"
            min={1}
            max={28}
            value={v.psDay}
            onChange={(e) => setV({ ...v, psDay: e.target.value })}
          />
        </Field>
        <Field label="Panel statements: time">
          <Input
            type="time"
            value={v.psTime}
            onChange={(e) => setV({ ...v, psTime: e.target.value })}
          />
        </Field>
      </div>
    </Dialog>
  );
}
